import React, { useState } from 'react'
import { ChevronDown, ChevronRight, EyeOff } from 'lucide-react'
import { money } from '../financial/formatting'
import Section, { Muted } from './Section'
import { Tag } from './StatusPill'
import { JsonBlock } from './JsonViewer'
import { OTHER_BUYER_LABEL, buyerReasonLabel, fmtLatency, outcomeLabel, outcomeTone } from './model'

// One card per buyer, in the order routing reached them. A card with a
// recorded response opens it; the backend only keeps decline and error
// bodies so far, so most cards say the full response is not stored.
//
// A hidden buyer is "Other buyer · details hidden": order and result only.
// Whatever else the entry carries is ignored, name included, so a backend
// slip can never put a hidden buyer's name or answer on this screen.
export default function BuyerResponses({ buyers }) {
  const [open, setOpen] = useState(null)
  const sorted = [...buyers].sort((a, b) => a.order - b.order)
  return (
    <Section id="li-buyers" title="Buyer responses" subtitle={sorted.length ? `${sorted.length} in the order routing reached them` : undefined}>
      {sorted.length === 0 ? <Muted>No buyer was contacted.</Muted> : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map((b) => (
            <li key={b.order}>
              {b.hidden
                ? <HiddenBuyer entry={b} />
                : <BuyerCard entry={b} open={open === b.order} onToggle={() => setOpen((o) => (o === b.order ? null : b.order))} />}
            </li>
          ))}
        </ol>
      )}
    </Section>
  )
}

function Order({ n }) {
  return (
    <span aria-label={`Buyer ${n}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, fontSize: 11, fontWeight: 700, flexShrink: 0, color: 'var(--text-secondary)', background: 'var(--bg-raised)', border: '1px solid var(--border)' }}>
      {n}
    </span>
  )
}

function HiddenBuyer({ entry }) {
  const result = outcomeLabel(entry.result)
  return (
    <div className="li-buyer" data-testid="buyer-card" data-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <Order n={entry.order} />
      <span style={{ fontWeight: 600 }}>{OTHER_BUYER_LABEL}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
        <EyeOff size={12} aria-hidden="true" /> details hidden
      </span>
      <span style={{ marginLeft: 'auto' }}><Tag tone={outcomeTone(entry.result)}>{result.label}</Tag></span>
    </div>
  )
}

function BuyerCard({ entry, open, onToggle }) {
  const result = outcomeLabel(entry.result)
  const reason = buyerReasonLabel(entry.reason)
  const hasResponse = entry.response !== null && entry.response !== undefined
  const metrics = [
    entry.bid !== null && entry.bid !== undefined && ['Bid', money(entry.bid)],
    fmtLatency(entry.latency_ms) && ['Latency', fmtLatency(entry.latency_ms)],
    entry.http_status && ['HTTP', String(entry.http_status)],
  ].filter(Boolean)

  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Order n={entry.order} />
        <span style={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{entry.buyer?.name}</span>
        <Tag tone={outcomeTone(entry.result)}>{result.label}</Tag>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {metrics.map(([k, v]) => (
            <span key={k}><span style={{ color: 'var(--text-muted)' }}>{k} </span>{v}</span>
          ))}
        </span>
        {hasResponse && (open
          ? <ChevronDown size={16} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
          : <ChevronRight size={16} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />)}
      </div>
      {reason && (
        <p style={{ margin: '6px 0 0 32px', fontSize: 13, color: 'var(--text-secondary)', overflowWrap: 'anywhere' }} title={reason !== entry.reason ? entry.reason : undefined}>
          {reason}
        </p>
      )}
      {!hasResponse && (
        <p style={{ margin: '4px 0 0 32px', fontSize: 11, color: 'var(--text-muted)' }}>Full response not stored yet</p>
      )}
    </>
  )

  if (!hasResponse) {
    return <div className="li-buyer" data-testid="buyer-card" data-hidden="false">{body}</div>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button type="button" className="li-buyer" data-testid="buyer-card" data-hidden="false" aria-expanded={open} onClick={onToggle}>
        {body}
      </button>
      {open && <JsonBlock value={entry.response} label={`${entry.buyer?.name || 'Buyer'} response JSON`} />}
    </div>
  )
}
